import React from 'react';
import PropTypes from 'prop-types';

const supportMultiple =
  typeof document !== 'undefined' && document && document.createElement
    ? 'multiple' in document.createElement('input')
    : true;

export default class Dropzone extends React.PureComponent {
  static PropTypes = {
    onDrop: PropTypes.func.isRequired,
    onDragEnter: PropTypes.func,
    onDragLeave: PropTypes.func,

    children: PropTypes.node,
    className: PropTypes.string,

    accept: PropTypes.string,
    maxSize: PropTypes.number,
  };

  static defaultProps = {
    maxSize: Infinity,
  };

  static childContextTypes = {
    selectFiles: PropTypes.func.isRequired,
  };

  getChildContext() {
    return {
      selectFiles: this.open,
    };
  }

  componentDidMount() {
    if (supportMultiple) {
      ['webkitdirectory', 'mozdirectory', 'msdirectory', 'odirectory', 'directory'].forEach(attribute => {
        this.directoryInputEl.setAttribute(attribute, true);
      });
    }
  }

  onDragOver = e => {
    e.preventDefault();
    e.stopPropagation();

    try {
      e.dataTransfer.dropEffect = 'copy';
    } catch (err) {
      // continue regardless of error
    }

    return false;
  };

  onDrop = e => {
    e.preventDefault();

    const droppedFiles = e.dataTransfer ? e.dataTransfer.files : e.target.files;
    const dataTransferItems = e.dataTransfer && e.dataTransfer.items ? e.dataTransfer.items : [];
    let files = [];

    // Add files
    const filesLength = droppedFiles.length;
    for (let i = 0; i < filesLength; i++) {
      files.push(droppedFiles[i]);
    }

    // Walk directories
    const walkers = [];
    const itemsLength = dataTransferItems.length;
    for (let i = 0; i < itemsLength; i++) {
      const item = dataTransferItems[i];

      if (typeof item.webkitGetAsEntry === 'function') {
        const entry = item.webkitGetAsEntry();

        if (entry && entry.isDirectory) {
          walkers.push(
            new Promise(resolve => {
              this.walkDirectory(entry.filesystem.root, walkedFiles => {
                files = files.concat(walkedFiles);
                resolve();
              });
            })
          );
        }
      }
    }

    Promise.all(walkers).then(() => {
      const acceptedFiles = [];
      const rejectedFiles = [];

      files.forEach(file => {
        if (!this.fileAccepted(file) || file.size > this.props.maxSize) {
          rejectedFiles.push(file);
        } else {
          acceptedFiles.push(file);
        }
      });

      this.props.onDrop(acceptedFiles, rejectedFiles, e);
    });

    return false;
  };

  fileAccepted = file => {
    const { accept } = this.props;

    if (file && accept) {
      const acceptedFilesArray = Array.isArray(accept) ? accept : accept.split(',');
      const fileName = file.name || '';
      const mimeType = file.type || '';
      const baseMimeType = mimeType.replace(/\/.*$/, '');

      return acceptedFilesArray.some(type => {
        const validType = type.trim();
        if (validType.charAt(0) === '.') {
          return fileName.toLowerCase().endsWith(validType.toLowerCase());
        } else if (/\/\*$/.test(validType)) {
          // This is something like a image/* mime type
          return baseMimeType === validType.replace(/\/.*$/, '');
        }
        return mimeType === validType;
      });
    }

    return true;
  };

  open = () => {
    this.fileInputEl.value = null;
    this.fileInputEl.click();
  };

  walkDirectory = (directory, callback) => {
    let results = [];

    if (directory === null) {
      return callback(results);
    }

    return this.readEntries(directory, (err, result) => {
      if (err) {
        return callback(err);
      }

      const entries = result.slice();

      const processEntry = () => {
        const current = entries.shift();

        if (current === undefined) {
          return callback(results);
        }

        if (current.isDirectory) {
          return this.walkDirectory(current, nestedResults => {
            results = results.concat(nestedResults);
            processEntry();
          });
        }

        return current.file(file => {
          results.push(file);
          return processEntry();
        }, processEntry);
      };

      return processEntry();
    });
  };

  readEntries = (directory, callback, readerSupplied) => {
    let entries = [];
    // reader should not be present on initial call
    const reader = readerSupplied || directory.createReader();

    return reader.readEntries(results => {
      if (!results.length) {
        return callback(null, entries);
      }

      entries = entries.concat(this.toArray(results));
      return this.readEntries(
        directory,
        (err, additionalEntries) => {
          if (err) {
            return callback(err);
          }

          entries = entries.concat(additionalEntries);
          return callback(null, entries);
        },
        reader
      );
    }, callback);
  };

  toArray(obj) {
    return Array.prototype.slice.call(obj || [], 0);
  }

  render() {
    const { accept, maxSize, ...props } = this.props;

    const inputAttributes = {
      accept,
      type: 'file',
      style: { display: 'none' },
      multiple: supportMultiple,
      onChange: this.onDrop,
    };

    return (
      <div {...props} onDragOver={this.onDragOver} onDrop={this.onDrop}>
        {this.props.children}
        <input {...inputAttributes} ref={el => (this.directoryInputEl = el)} />
        <input {...inputAttributes} ref={el => (this.fileInputEl = el)} />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./app/components/dropzone/index.js